import find from 'lodash/find.js';
import has from 'lodash/has.js';
import map from 'lodash/map.js';
import toArray from 'lodash/toArray.js';
import uniqBy from 'lodash/uniqBy.js';

import { DELETED_CHAT_USER } from '@/constants/chat.js';
import { handleResponseError } from '@/Helpers.js';
import axios from '@/plugins/axios.js';
import moment from '@/plugins/vue-moment.js';

const defaultPaginator = {
    page: 1,
    perPage: 50,
    sorting: 'desc',
};

export const transformMessage = ({ id, type, sender, body, created_at }) => ({
    id,
    type,
    // A deleted sender's account arrives as null.
    author: sender?.id ?? null,
    data: {
        text: body,
    },
    created_at: moment.utc(created_at),
});

const transformUser = user => {
    if (!user) return { ...DELETED_CHAT_USER };

    const { id, name, photo_url } = user;

    return { id, name, photo_url };
};

const messageSort = (a, b) => moment(a.created_at).valueOf() - moment(b.created_at).valueOf();

/**
 * Full inbox loads page through every conversation, so overlapping loads multiply memory for
 * users with many conversations. Callers (the chat launcher's route watcher, the Messages
 * widget) share one running load; a call made while it runs queues a single follow-up load so
 * the caller still sees data fetched after its request, such as unread counts after mark-read.
 */
let activeConversationsFetch = null;
let queuedConversationsFetch = null;

/**
 * Upper bound for one inbox page request attempt, matching the server's 30-second request
 * limit. The axios plugin retries a timed-out request a few times with backoff, so a stalled
 * page fails within a couple of minutes instead of holding the serialized load queue forever.
 */
const CONVERSATIONS_PAGE_TIMEOUT_MS = 30000;

/**
 * Clear the module-scope inbox load state (tests and HMR).
 */
export function resetConversationsFetchState() {
    activeConversationsFetch = null;
    queuedConversationsFetch = null;
}

function startConversationsFetch(context) {
    const load = loadAllConversations(context).finally(() => {
        if (activeConversationsFetch === load) {
            activeConversationsFetch = null;
        }
    });

    activeConversationsFetch = load;

    return load;
}

async function loadAllConversations({ commit, state }) {
    commit('TOGGLE_CONVERSATIONS_LOADING');

    try {
        const conversations = [];
        let page = 1;
        let lastPage = 1;

        do {
            const response = await axios.get('/api/conversations', {
                params: { page, per_page: 100 },
                timeout: CONVERSATIONS_PAGE_TIMEOUT_MS,
            });

            if (!response) {
                commit('ERROR_LOADING_CONVERSATIONS', true);

                return;
            }

            conversations.push(...toArray(response.data?.data));
            lastPage = Number(response.data?.meta?.last_page ?? 1);
            page += 1;
        } while (page <= lastPage && page <= 50);

        commit('ERROR_LOADING_CONVERSATIONS', false);

        const mappedConversations = conversations.map(conversation => {
            Object.keys(conversation.data).map(key => (conversation[key] = conversation.data[key]));

            const participants = conversation.participants.map(({ messageable }) => transformUser(messageable));

            commit('SET_PARTICIPANTS', {
                conversationId: conversation.id,
                participants,
            });

            return conversation;
        });

        commit('SET_CONVERSATIONS', mappedConversations);

        if (!state.currentConversation) {
            const sortedConversations = conversations.sort((a, b) => {
                const dateA = new Date(a.updated_at);
                const dateB = new Date(b.updated_at);
                return dateB - dateA;
            });

            if (has(sortedConversations, 0)) {
                commit('SET_CURRENT_CONVERSATION', sortedConversations[0].id);
            }
        }

        commit('SET_CONVERSATIONS_FETCHED', true);
    } catch (error) {
        // Before the first successful load, a failed or timed-out page request shows the chat
        // launcher's "error loading" state. After it, the open chat keeps its last loaded list.
        if (!state.conversationsFetched) {
            commit('ERROR_LOADING_CONVERSATIONS', true);
        }

        throw error;
    } finally {
        commit('TOGGLE_CONVERSATIONS_LOADING');
    }
}

export default {
    state: {
        loading: false,

        isChatOpen: false,
        newMessagesCount: 0,

        currentConversation: null,

        conversations: [],
        conversationsLoading: false,

        /**
         * A conversation another part of the app asked the chat to open, as `{ id }`. Each request
         * is a new object, so asking for the same conversation again still opens it.
         */
        requestedConversation: null,

        participants: {},
        participantsLoading: false,

        messages: {},
        messageCounts: {},
        messagesLoading: false,

        messagePaginator: defaultPaginator,

        online: {},

        unknownConversationListener: null,

        errorLoadingConversations: false,

        conversationsFetched: false,
    },

    getters: {
        conversations: state =>
            state.conversations.sort((a, b) => moment(b.updated_at).valueOf() - moment(a.updated_at).valueOf()),

        conversation: state => conversationId =>
            find(state.conversations, conversation => conversation.id === parseInt(conversationId, 10)),

        locateConversation: (state, getters, rootState, rootGetters) => subjectMessageableId =>
            find(state.conversations, ({ participants }) => {
                const messageableIds = map(participants, 'messageable.id');

                return (
                    messageableIds.includes(subjectMessageableId) && messageableIds.includes(rootGetters['Auth/userId'])
                );
            }),

        conversationCount: state => state.conversations.length,

        totalUnread: state => state.conversations.reduce((prev, cur) => prev + cur.unread, 0),
        messages: state =>
            state.messages[state.currentConversation]
                ? uniqBy(state.messages[state.currentConversation].sort(messageSort), 'id')
                : [],
        messageCount: getters => getters.messages.length,
        participants: state => state.participants[state.currentConversation] || [],

        loaded: state => state.conversationsFetched,
    },

    actions: {
        openChat: ({ commit }) => {
            commit('SET_CHAT_OPEN', true);
            commit('SET_NEW_MESSAGES_COUNT', 0);
        },

        closeChat: ({ commit }) => {
            commit('SET_CHAT_OPEN', false);
            commit('SET_REQUESTED_CONVERSATION', null);
        },

        /**
         * Open the chat on one conversation's messages instead of the conversation list.
         * @param context
         * @param conversationId
         */
        openConversationInChat({ commit, dispatch }, conversationId) {
            commit('SET_REQUESTED_CONVERSATION', { id: conversationId });
            dispatch('openChat');
        },

        unknownConversation({ state }, id) {
            if (typeof state.unknownConversationListener === 'function') {
                state.unknownConversationListener(id);
            }
        },

        fetchConversations(context) {
            if (!activeConversationsFetch) {
                return startConversationsFetch(context);
            }

            if (!queuedConversationsFetch) {
                const queued = activeConversationsFetch
                    .catch(() => {})
                    .then(() => {
                        if (queuedConversationsFetch === queued) {
                            queuedConversationsFetch = null;
                        }

                        // A load started after this caller queued is already fresh enough to share.
                        return activeConversationsFetch ?? startConversationsFetch(context);
                    });

                queuedConversationsFetch = queued;
            }

            return queuedConversationsFetch;
        },

        async fetchMessages({ commit, state }) {
            if (state.currentConversation) {
                commit('TOGGLE_MESSAGES_LOADING');

                try {
                    const response = await axios.get(`/api/conversations/${state.currentConversation}/messages`, {
                        params: state.messagePaginator,
                        errorHandle: false,
                    });

                    if (!response) {
                        commit('ERROR_LOADING_CONVERSATIONS', true);

                        return;
                    }

                    commit('ERROR_LOADING_CONVERSATIONS', false);

                    const messages = response.data.data.map(transformMessage);

                    commit('SET_MESSAGES', {
                        conversationId: state.currentConversation,
                        messages,
                    });

                    commit('SET_MESSAGE_COUNT', {
                        conversationId: state.currentConversation,
                        count: response.data.meta.total,
                    });
                } catch (error) {
                    handleResponseError(error);
                } finally {
                    commit('TOGGLE_MESSAGES_LOADING');
                }
            }
        },

        async fetchMessageHistory({ commit, state }) {
            commit('TOGGLE_MESSAGES_LOADING');

            commit('ADVANCE_MESSAGE_PAGINATOR');

            try {
                const { data } = await axios.get(`/api/conversations/${state.currentConversation}/messages`, {
                    params: state.messagePaginator,
                    errorHandle: false,
                });

                const messages = data.data.map(transformMessage);

                commit('PREPEND_MESSAGES', {
                    conversationId: state.currentConversation,
                    messages,
                });
            } catch (error) {
                handleResponseError(error);
            } finally {
                commit('TOGGLE_MESSAGES_LOADING');
            }
        },

        async openNewConversation({ commit, dispatch, state, getters, rootGetters }, participantId) {
            const authUser = rootGetters['Auth/user'];

            if (authUser.id === participantId) {
                return;
            }

            dispatch('openChat');

            const locateConversation = getters.locateConversation(participantId);

            if (locateConversation) {
                await dispatch('onConversationSwitch', locateConversation.id);
            } else {
                commit('TOGGLE_CONVERSATIONS_LOADING');

                try {
                    const chatResponse = await axios.get(`/api/conversations?with=${participantId}`, {
                        errorHandle: false,
                    });

                    let conversationId;

                    if (has(chatResponse.data.data, 'id')) {
                        commit('ADD_NEW_CONVERSATION', chatResponse.data.data);

                        await dispatch('onConversationSwitch', chatResponse.data.data.id);

                        conversationId = chatResponse.data.data.id;
                    } else {
                        conversationId = await dispatch('createConversation', participantId);
                    }

                    if (typeof state.unknownConversationListener === 'function') {
                        state.unknownConversationListener(conversationId);
                    }
                } catch (error) {
                    handleResponseError(error);
                } finally {
                    commit('TOGGLE_CONVERSATIONS_LOADING');
                }
            }
        },

        async createConversation({ commit }, participantId) {
            try {
                const createConversation = await axios.post(
                    '/api/conversations',
                    {
                        participants: [participantId],
                    },
                    { errorHandle: false }
                );

                const conversation = createConversation.data.data;

                const conversationId = conversation.id;

                const participants = conversation.participants.map(({ messageable }) => transformUser(messageable));

                commit('SET_PARTICIPANTS', {
                    conversationId: conversation.id,
                    participants,
                });

                conversation.unread = 0;

                commit('ADD_NEW_CONVERSATION', conversation);
                commit('SET_CURRENT_CONVERSATION', conversation.id);

                commit('SET_MESSAGES', {
                    conversationId: conversation.id,
                    messages: [],
                });

                return conversationId;
            } catch (error) {
                handleResponseError(error);

                return null;
            }
        },

        async openLatestConversation({ dispatch }) {
            await dispatch('fetchConversations');

            return dispatch('fetchMessages');
        },

        addMessage({ commit }, payload) {
            commit('ADD_MESSAGE', payload);
        },

        messageSent({ commit, dispatch, state }, message) {
            commit('ADD_MESSAGE', {
                conversationId: state.currentConversation,
                message,
            });

            dispatch('sendMessage', message.data.text);
        },

        sendMessage({ state, commit, dispatch }, text) {
            if (text.length > 0) {
                const count = state.isChatOpen ? state.newMessagesCount : state.newMessagesCount + 1;

                commit('SET_NEW_MESSAGES_COUNT', count);

                dispatch('onMessageWasSent', {
                    author: 'support',
                    type: 'text',
                    data: { text },
                });
            }
        },

        onMessageWasSent({ state, commit, dispatch }, message) {
            axios
                .post(
                    `/api/conversations/${state.currentConversation}/messages`,
                    {
                        message: message.data.text,
                    },
                    { errorHandle: false }
                )
                .then(data => {
                    commit('ADD_MESSAGE', {
                        conversationId: state.currentConversation,
                        message: transformMessage(data.data),
                    });

                    commit('UPDATE_CONVERSATION_TIMESTAMP', state.currentConversation);
                    commit('INCREMENT_MESSAGES_COUNT', state.currentConversation);
                })
                .catch(error => {
                    if (error.response.data.exception === 'Pusher\\PusherException') {
                        dispatch('fetchMessages');
                    } else {
                        handleResponseError(error);
                    }
                });
        },

        setCurrentConversation({ dispatch, commit }, conversationId) {
            commit('SET_CURRENT_CONVERSATION', conversationId);

            dispatch('markConversationAsRead', conversationId);
        },

        async onConversationSwitch({ commit, dispatch, state }, conversationId) {
            await dispatch('setCurrentConversation', conversationId);

            const conversation = find(state.conversations, findConversation => findConversation.id === conversationId);

            const participants = conversation.participants.map(({ messageable }) => transformUser(messageable));

            commit('SET_PARTICIPANTS', {
                conversationId,
                participants,
            });

            commit('RESET_MESSAGE_PAGINATOR');

            const loadMessages = await dispatch('fetchMessages');

            await dispatch('markConversationAsRead', conversationId);

            return loadMessages;
        },

        listenToConversation({ state }, conversationId) {
            const currentConversationId = conversationId || state.currentConversation;

            if (currentConversationId) {
                this.chatChannel = this.$echo.join(`chat-${currentConversationId}`);

                this.chatChannel
                    .listen('ConversationMessageSent', async e => {
                        this.showTypingIndicator = null;

                        const { data } = await axios.get(
                            `/api/conversations/${currentConversationId}/messages/${e.id}`
                        );

                        this.addMessage({
                            conversationId: e.conversation.id,
                            message: data.data,
                        });
                    })
                    .listenForWhisper('typing', e => {
                        this.showTypingIndicator = e.user.id;
                        setTimeout(() => (this.showTypingIndicator = null), 3000);
                    });
            }
        },

        markConversationAsRead({ commit, getters }, conversationId) {
            const conversation = getters.conversation(conversationId);

            if (conversation && conversation.unread > 0) {
                const cachedUnread = conversation.unread;

                commit('SET_CONVERSATION_READ', { conversationId });

                return axios.put(`/api/conversations/${conversationId}/mark-as-read`).catch(() =>
                    commit('SET_CONVERSATION_READ', {
                        conversationId,
                        count: cachedUnread,
                    })
                );
            }

            return Promise.resolve();
        },

        async receiveNewMessage({ state, getters, dispatch, commit }, { conversation, currentWindow }) {
            if (!getters.conversation(conversation.id)) {
                // If the conversation is new, add it and initialize its messages array
                commit('ADD_NEW_CONVERSATION', conversation);
                commit('SET_MESSAGES', {
                    conversationId: conversation.id,
                    messages: [],
                });
            } else {
                // If the conversation already exists, update its timestamp and ensure its messages array is initialized
                commit('UPDATE_CONVERSATION_TIMESTAMP', conversation.id);
                if (!state.messages[conversation.id]) {
                    commit('SET_MESSAGES', {
                        conversationId: conversation.id,
                        messages: [],
                    });
                }
            }

            const { data } = await axios.get(`/api/conversations/${conversation.id}/messages`);

            data.data.map(transformMessage).forEach(message => {
                commit('ADD_MESSAGE', {
                    conversationId: conversation.id,
                    message,
                });
            });

            commit('INCREMENT_CONVERSATION_UNREAD', conversation.id);
            commit('INCREMENT_MESSAGES_COUNT', conversation.id);

            if (state.isChatOpen && state.currentConversation === conversation.id && currentWindow === 'messages') {
                dispatch('markConversationAsRead', conversation.id);
            }
        },
    },

    mutations: {
        SET_CHAT_OPEN(state, value) {
            state.isChatOpen = value;
        },

        SET_NEW_MESSAGES_COUNT(state, count) {
            state.newMessagesCount = count;
        },

        TOGGLE_CONVERSATIONS_LOADING(state) {
            state.conversationsLoading = !state.conversationsLoading;
        },

        SET_CONVERSATIONS(state, conversations) {
            state.conversations = conversations;
        },

        SET_CURRENT_CONVERSATION(state, conversation) {
            state.currentConversation = conversation;
        },

        ERROR_LOADING_CONVERSATIONS(state, error) {
            state.errorLoadingConversations = error;
        },

        SET_CONVERSATIONS_FETCHED(state, value) {
            state.conversationsFetched = value;
        },

        ADD_NEW_CONVERSATION(state, conversation) {
            state.conversations.unshift(conversation);
        },

        UPDATE_CONVERSATION_TIMESTAMP(state, conversationId) {
            const index = state.conversations.findIndex(conversation => conversation.id === conversationId);
            state.conversations[index].updated_at = moment.utc().toISOString();
        },

        INCREMENT_CONVERSATION_UNREAD(state, conversationId) {
            const index = state.conversations.findIndex(conversation => conversation.id === conversationId);
            state.conversations[index].unread = (state.conversations[index].unread || 0) + 1;
        },

        SET_CONVERSATION_READ(state, { conversationId, count }) {
            const index = state.conversations.findIndex(conversation => conversation.id === conversationId);
            state.conversations[index].unread = count || 0;
        },

        TOGGLE_PARTICIPANTS_LOADING(state) {
            state.participantsLoading = !state.participantsLoading;
        },

        SET_PARTICIPANTS(state, { conversationId, participants }) {
            state.participants[conversationId] = participants;
        },

        TOGGLE_MESSAGES_LOADING(state) {
            state.messagesLoading = !state.messagesLoading;
        },

        SET_MESSAGES(state, { conversationId, messages }) {
            state.messages[conversationId] = messages;
        },

        SET_MESSAGE_COUNT(state, { conversationId, count }) {
            state.messageCounts[conversationId] = count;
        },

        INCREMENT_MESSAGES_COUNT(state, conversationId) {
            state.messageCounts[conversationId] = (state.messageCounts[conversationId] || 0) + 1;
        },

        ADVANCE_MESSAGE_PAGINATOR(state) {
            state.messagePaginator.page += 1;
        },

        RESET_MESSAGE_PAGINATOR(state) {
            state.messagePaginator = defaultPaginator;
        },

        PREPEND_MESSAGES(state, { conversationId, messages }) {
            state.messages[conversationId] = [...messages, ...state.messages[conversationId]];
        },

        ADD_MESSAGE(state, { conversationId, message }) {
            if (state.messages[conversationId]) {
                // Check if the message already exists in the array
                const exists = state.messages[conversationId].some(
                    existingMessage => existingMessage.id === message.id
                );
                if (!exists) {
                    state.messages[conversationId].push(message);
                }
            }
        },

        ADD_ONLINE_USER(state, user) {
            state.online[user.id] = user;
        },

        REMOVE_ONLINE_USER(state, userId) {
            delete state.online[userId];
        },

        SET_UNKNOWN_CONVERSATION_LISTENER(state, listener) {
            state.unknownConversationListener = listener;
        },

        SET_REQUESTED_CONVERSATION(state, requestedConversation) {
            state.requestedConversation = requestedConversation;
        },
    },
};
